import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import { useAuth } from "@/app/providers/AuthProvider";

const SCOPE_PARAM = "company";
const SCOPE_STORAGE_KEY = "altek.scope.legalEntity";

/**
 * Which company the screen is looking at.
 *
 * THE URL IS THE TRUTH, and that is a deliberate cost. A remembered-in-localStorage scope
 * makes every link a lie: the sender pastes "the list I am looking at" and the recipient
 * opens the same path against a different company, sees different rows, and neither of them
 * knows why. Putting it in the query string means a pasted link shows the sender's data.
 *
 * `localStorage` still has a job — it answers "which company did I have open yesterday" for a
 * URL that carries no scope. It is a default, never an override: a URL that names a company
 * wins, always.
 *
 * VALIDATED ON EVERY READ against the session's own grants. A company the caller may no
 * longer read must not be sent to the server just because a stale bookmark names it — the
 * request would 404 and the screen would look broken rather than re-scoped.
 */
export interface Scope {
  /** The active company, or null while the session is still loading. */
  legalEntityId: string | null;
  legalEntities: readonly { id: string; code: string; name: string }[];
  /** Writes the choice to the URL, and remembers it for the next visit. */
  setScope: (id: string) => void;
  /** True when the caller can read more than one company — decides if the selector appears. */
  canSwitch: boolean;
}

export function useScope(): Scope {
  const { session } = useAuth();
  const [params, setParams] = useSearchParams();

  const legalEntities = useMemo(() => session?.scopes.legalEntities ?? [], [session]);

  const legalEntityId = useMemo(() => {
    if (legalEntities.length === 0) return null;

    const fromUrl = params.get(SCOPE_PARAM);
    if (fromUrl && legalEntities.some((entity) => entity.id === fromUrl)) return fromUrl;

    let remembered: string | null = null;
    try {
      remembered = localStorage.getItem(SCOPE_STORAGE_KEY);
    } catch {
      /* private browsing: fall through to the first readable company */
    }
    if (remembered && legalEntities.some((entity) => entity.id === remembered)) return remembered;

    return legalEntities[0]?.id ?? null;
  }, [legalEntities, params]);

  const setScope = useCallback(
    (id: string) => {
      try {
        localStorage.setItem(SCOPE_STORAGE_KEY, id);
      } catch {
        /* applies to this session only */
      }
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(SCOPE_PARAM, id);
          /**
           * Paging is dropped on a scope change. Page 7 of one company's employees is not page
           * 7 of another's, and keeping the number would land the user on an empty page that
           * looks like "no records" rather than "wrong page".
           */
          next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return {
    legalEntityId,
    legalEntities,
    setScope,
    canSwitch: legalEntities.length > 1,
  };
}
